import type { BaseQueryFn, QueryActionCreatorResult, QueryDefinition } from '@reduxjs/toolkit/query';
import Button from 'components/actions/Button';
import ErrorDialog from 'components/overlays/ErrorDialog';
import { SystemAdminDialogTypes, SystemAdminFormTypes } from 'helper/types';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearActiveElements,
    getSystemAdminOpenDialog,
    setSystemAdminOpenDialog,
} from 'store/slices/settingsPageSlice';
import { clearFleetFormData, getSystemFleetsFormType } from 'store/slices/systemFleet';

import SystemTableHeader from '../common/SystemTableHeader';
import type { SystemTableColumn } from '../common/tableColumn';
import { getNextSortConfig, type SortConfig, sortItems } from '../common/tableSorting';
import ExitEditingDialog from '../ExitEditingDialog';
import DialogForm from '../FormDialog';
import NoContent from '../NoContent';
import TableError from '../TableError';
import TableSkeleton from '../TableSkeleton';
import SystemFleetsForm from './SystemFleetsForm';
import SystemFleetsRow from './SystemFleetsRow';

import type { SystemFleet } from 'API';

type SortKey = 'name' | 'numberOfVehicles' | 'createdAt';
type TableColumnKey = SortKey | 'teams' | '_';

const columns: SystemTableColumn<TableColumnKey, SortKey, SystemFleet>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'numberOfVehicles', label: 'No. of vehicles', sortable: true },
    {
        key: 'teams',
        label: 'Team assigned',
    },
    { key: 'createdAt', label: 'Created', sortable: true, sortValue: fleet => new Date(fleet.createdAt ?? '') },
    { key: '_', label: '' },
];

interface Props {
    data: SystemFleet[] | undefined;
    isLoading?: boolean;
    isFetching?: boolean;
    isError?: boolean;
    refetch(): QueryActionCreatorResult<
        QueryDefinition<string, BaseQueryFn<any, unknown, unknown, {}, {}>, never, SystemFleet[], 'systemFleets'>
    >;
}

const SystemFleetsTable = ({ data, isLoading, isFetching, isError, refetch }: Props) => {
    const dispatch = useDispatch();
    const systemAdminOpenDialog = useSelector(getSystemAdminOpenDialog);
    const isFormDialogOpen = systemAdminOpenDialog === SystemAdminDialogTypes.FLEET_DIALOG;
    const exitConfirmationIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.EXIT_CONFIRMATION;
    const errorDialogIsOpen = systemAdminOpenDialog === SystemAdminDialogTypes.ERROR_DIALOG;
    const systemFleetForm = useSelector(getSystemFleetsFormType);
    const [sortConfig, setSortConfig] = useState<SortConfig<SortKey>>({ key: 'name', dir: 'asc' });

    const setDialogHeader = useMemo(() => {
        switch (systemFleetForm) {
            case SystemAdminFormTypes.EDIT_FORM:
                return 'Edit fleet';
            case SystemAdminFormTypes.CREATE_FORM:
                return 'Add information about the new fleet';
            default:
                return;
        }
    }, [systemFleetForm]);

    const closeDialogs = () => {
        dispatch(setSystemAdminOpenDialog(undefined));
    };

    const openCancelDialog = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.EXIT_CONFIRMATION));
    };

    const exitEditing = () => {
        dispatch(clearFleetFormData());
        dispatch(clearActiveElements());
        closeDialogs();
    };

    const keepEditing = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.FLEET_DIALOG));
    };

    const sortedFleetData = useMemo(() => {
        if (!data) return [];
        return sortItems(data, sortConfig, columns);
    }, [data, sortConfig]);

    if (isLoading) {
        return <TableSkeleton id='fleets' tableHeaders={Object.fromEntries(columns.map(c => [c.key, c.label]))} />;
    }

    if (isError) {
        return (
            <div className='TableContentWrapper'>
                <TableError id='fleets' header='An error occured while fetching fleets' onClick={() => refetch()} />
            </div>
        );
    }

    if (!sortedFleetData.length) {
        return (
            <NoContent
                id='fleets'
                title='No fleets yet, but they will appear here'
                body='As of now, there are no fleets in the Control Center.'
                button={
                    <Button data-testid='system-admin-create-button-fleet' style='primary' disabled>
                        <span className='rioglyph rioglyph-plus' />
                        Add new fleet
                    </Button>
                }
            />
        );
    }

    return (
        <div className='display-flex flex-column' data-testid='system-fleets-table-wrapper'>
            <table
                data-testid='system-fleets-table'
                className='table-head-filled table-sticky-in-container table-hover table-bordered table'
            >
                <SystemTableHeader<TableColumnKey, SortKey, SystemFleet>
                    columns={columns}
                    sortConfig={sortConfig}
                    onSort={colKey => setSortConfig(prev => getNextSortConfig(prev, colKey))}
                />
                <tbody>
                    {sortedFleetData.map(_ => (
                        <SystemFleetsRow key={_.id} fleet={_} />
                    ))}
                </tbody>
            </table>
            <DialogForm
                id='edit-fleet'
                title={setDialogHeader}
                form={<SystemFleetsForm refetch={refetch} isFetchingFleets={isFetching} />}
                show={isFormDialogOpen}
                onCancel={openCancelDialog}
            />
            <ExitEditingDialog
                id='exit-fleet-edit'
                show={exitConfirmationIsOpen}
                onConfirm={exitEditing}
                onCancel={keepEditing}
            />
            <ErrorDialog show={errorDialogIsOpen} onConfirm={exitEditing} />
        </div>
    );
};

export default SystemFleetsTable;
